import { useCallback, useEffect, useState } from "react";
import {
  READINESS_TASK_IDS,
  START_GUIDE_AFTER_ORDER_PAGES,
  START_GUIDE_SALES_MODES,
  SMART_STORE_PRESENCES,
  START_GUIDE_STEPS,
  StartGuideReadinessTaskId,
  StartGuideState,
} from "@/features/pathpick/features/start-guide/types";
import {
  START_GUIDE_DEFAULT_STATE,
  START_GUIDE_READINESS_REVISION,
  START_GUIDE_STORAGE_KEY,
  START_GUIDE_STORAGE_VERSION,
} from "@/features/pathpick/features/start-guide/constants";

// 저장된 값은 사용자가 devtools로 고칠 수도 있고 배포 사이에 스키마가 바뀔 수도 있다.
// 그래서 읽을 때 한 번만 정규화하고, 그 결과만 상태로 쓴다 —
// 알 수 없는 값은 던지지 않고 조용히 기본값으로 되돌린다(전달서 §18).
function normalize(raw: unknown): StartGuideState {
  if (typeof raw !== "object" || raw === null) {
    return START_GUIDE_DEFAULT_STATE;
  }
  const value = raw as Partial<StartGuideState>;

  // 스키마 버전이 다르면 마이그레이션 대신 버린다 — 목 상태라 잃어도 손해가 없다.
  if (value.version !== START_GUIDE_STORAGE_VERSION) {
    return START_GUIDE_DEFAULT_STATE;
  }

  const isOneOf = <T extends string>(
    candidate: unknown,
    allowed: readonly T[]
  ): candidate is T =>
    typeof candidate === "string" &&
    (allowed as readonly string[]).includes(candidate);

  // 설정 단계 구성이 바뀌었으면 완료 상태만 버리고 판매 방식 선택은 남긴다.
  const isRevisionCurrent =
    value.readinessRevision === START_GUIDE_READINESS_REVISION;

  return {
    version: START_GUIDE_STORAGE_VERSION,
    readinessRevision: START_GUIDE_READINESS_REVISION,
    salesMode: isOneOf(value.salesMode, START_GUIDE_SALES_MODES)
      ? value.salesMode
      : START_GUIDE_DEFAULT_STATE.salesMode,
    hasSavedProfile: value.hasSavedProfile === true,
    smartStorePresence: isOneOf(value.smartStorePresence, SMART_STORE_PRESENCES)
      ? value.smartStorePresence
      : null,
    isPreflightConfirmed:
      isRevisionCurrent && value.isPreflightConfirmed === true,
    completedTasks:
      isRevisionCurrent && Array.isArray(value.completedTasks)
        ? (value.completedTasks.filter((task) =>
            isOneOf(task, READINESS_TASK_IDS)
          ) as StartGuideReadinessTaskId[])
        : [],
    currentStep: isOneOf(value.currentStep, START_GUIDE_STEPS)
      ? value.currentStep
      : START_GUIDE_DEFAULT_STATE.currentStep,
    afterOrderPage: isOneOf(value.afterOrderPage, START_GUIDE_AFTER_ORDER_PAGES)
      ? value.afterOrderPage
      : START_GUIDE_DEFAULT_STATE.afterOrderPage,
    freeTrialStartedAt:
      typeof value.freeTrialStartedAt === "string"
        ? value.freeTrialStartedAt
        : null,
  };
}

function read(): StartGuideState {
  try {
    const stored = window.sessionStorage.getItem(START_GUIDE_STORAGE_KEY);
    return stored ? normalize(JSON.parse(stored)) : START_GUIDE_DEFAULT_STATE;
  } catch {
    // 파싱 실패든 저장소 접근 차단(사파리 프라이빗 등)이든 가이드는 계속 돌아야 한다.
    return START_GUIDE_DEFAULT_STATE;
  }
}

/**
 * 시작 가이드의 탭 세션 상태.
 *
 * sessionStorage 라서 탭을 닫으면 사라진다 — 가이드는 한 번 훑고 나가는 화면이고,
 * 다음에 다시 열었을 때 예전 진행이 남아 있는 편이 오히려 헷갈린다.
 * SSR에서는 저장소가 없어 항상 기본값으로 첫 렌더를 맞추고,
 * 마운트 후에 저장값을 읽어 hydration 불일치를 피한다.
 */
export default function useStartGuideStorage() {
  const [state, setState] = useState<StartGuideState>(
    START_GUIDE_DEFAULT_STATE
  );
  // 저장값을 읽기 전에 쓰기가 일어나면 기본값으로 덮어써 버린다 — 읽은 뒤부터 저장한다.
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setState(read());
    setIsHydrated(true);
    try {
      // 예전 버전이 localStorage 에 남겨 둔 값 — 이제 읽지 않으니 한 번 치운다.
      window.localStorage.removeItem(START_GUIDE_STORAGE_KEY);
    } catch {
      // 저장소 접근이 막혀 있어도 가이드는 계속 돌아야 한다.
    }
  }, []);

  useEffect(() => {
    if (!isHydrated) {
      return;
    }
    try {
      window.sessionStorage.setItem(
        START_GUIDE_STORAGE_KEY,
        JSON.stringify(state)
      );
    } catch {
      // 저장에 실패해도 화면 동작은 막지 않는다(세션 안에서는 메모리 상태로 유지된다).
    }
  }, [state, isHydrated]);

  const patch = useCallback((changes: Partial<StartGuideState>) => {
    setState((prev) => ({ ...prev, ...changes }));
  }, []);

  const completeTask = useCallback((task: StartGuideReadinessTaskId) => {
    setState((prev) =>
      prev.completedTasks.includes(task)
        ? prev
        : { ...prev, completedTasks: [...prev.completedTasks, task] }
    );
  }, []);

  // 개발/목 환경 전용 — 저장값을 통째로 버리고 처음 방문한 것과 같은 상태로 되돌린다.
  // 지운 직후 저장 effect 가 기본값을 다시 써넣는데, 그게 첫 방문자가 갖는 값과 같다.
  const resetTestValues = useCallback(() => {
    try {
      window.sessionStorage.removeItem(START_GUIDE_STORAGE_KEY);
    } catch {
      // 저장소 접근이 막혀 있어도 메모리 상태는 되돌린다.
    }
    setState(START_GUIDE_DEFAULT_STATE);
  }, []);

  return { state, isHydrated, patch, completeTask, resetTestValues };
}
